import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type {
  GenericPageBackgroundImagePosition,
  GenericPageBackgroundImageSize,
  GenericPageBackgroundRepeat,
  GenericPageConfig,
} from '@front-platform/content-builder';
import {
  genericPageBackgroundSizeCss,
  genericPageResolveConfig,
  normalizeGenericPageBackgroundImagePosition,
  normalizeGenericPageBackgroundImageSize,
  normalizeGenericPageBackgroundRepeat,
} from '@front-platform/content-builder';
import { Component, ElementRef, OnInit, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { Meta } from '@angular/platform-browser';
export interface GenericPageRuntimeConfig {
  'body'?: any;
  'footer'?: any;
}

export type GenericPageConfigInput = Partial<
  Omit<GenericPageConfig, keyof GenericPageRuntimeConfig> & GenericPageRuntimeConfig
>;
export type GenericPageResolvedConfig = Omit<GenericPageConfig, keyof GenericPageRuntimeConfig> &
  Partial<GenericPageRuntimeConfig>;
export interface GenericPageContract extends ConfigComponentContract<
  GenericPageConfigInput,
  GenericPageResolvedConfig
> {}

@Component({
  selector: 'app-generic-page',
  templateUrl: './generic-page.component.html',
  styleUrls: ['./generic-page.component.scss'],
  standalone: false,
})
export class GenericPageComponent implements OnInit, GenericPageContract {
  readonly config = input<GenericPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<GenericPageConfig, GenericPageRuntimeConfig>(
    genericPageResolveConfig,
    () => ({ config: this.config() }),
  );
  get body(): any {
    return this.resolvedConfig()['body'] as unknown as any;
  }
  get footer(): any {
    return this.resolvedConfig()['footer'] as unknown as any;
  }

  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get backgroundImagePosition(): GenericPageBackgroundImagePosition {
    return normalizeGenericPageBackgroundImagePosition(
      this.resolvedConfig()['background_image_position'],
    );
  }
  get backgroundImageSize(): GenericPageBackgroundImageSize {
    return normalizeGenericPageBackgroundImageSize(
      this.resolvedConfig()['background_image_size'],
    );
  }
  get backgroundRepeat(): GenericPageBackgroundRepeat {
    return normalizeGenericPageBackgroundRepeat(this.resolvedConfig()['background_repeat']);
  }
  get backgroundSizeCss(): string | null {
    if (!this.backgroundImage) {
      return null;
    }
    return genericPageBackgroundSizeCss(this.backgroundImageSize);
  }
  get backgroundRepeatCss(): string | null {
    if (!this.backgroundImage) {
      return null;
    }
    return this.backgroundRepeat;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }

  get kioskDisplay(): boolean {
    return this.resolvedConfig()['kiosk_display'] as unknown as boolean;
  }

  get metaDescription(): string | undefined {
    return this.resolvedConfig()['meta_description'] as unknown as string | undefined;
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHistoryService: NavigationHistoryService,
    private el: ElementRef,
    private readonly meta: Meta,
  ) {}

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      '/' + this.activatedRoute.snapshot.url.join('/'),
      this.activatedRoute.snapshot,
    );

    if (this.textColor?.value) {
      this.setTextOverrideColor();
    }
    if (this.metaDescription) {
      this.meta.updateTag({
        name: 'description',
        content: this.metaDescription,
      });
    }
  }

  setTextOverrideColor(): void {
    const textColor = this.textColor?.value;
    this.el.nativeElement.style.setProperty('--my-class-bg-color', textColor);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(GenericPageComponent, { 'keyAliases': {} });
